import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "@libs/zapi-ui/shadcn";
import { AppButton, Typography, LoadingSpinner, ConfirmationModal } from "@libs/zapi-ui/custom";
import { XIcon } from "@phosphor-icons/react";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useState, useCallback } from "react";
import { FileItem } from "../../Repository/FileItem";
import { UploadFilesButton } from "../../Repository/_UploadFilesButton";
import { useDocumentsUsefulness } from "../../Repository/hooks/useDocumentsUsefulness";
import { useContentPreferences } from "../../Repository/hooks/useContentPreferences";
import { formatCharacterLength } from "@/zapi-frontend/shared/lib/formatCharacterLength";

export interface VaultDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  chapterId?: string;
}

export function VaultDrawer({ open, onOpenChange, projectId, chapterId = "" }: VaultDrawerProps) {
  const shouldFetchDocuments = !!open && !!projectId;

  const {
    documents,
    isLoading,
    createDocument,
    createWebLink,
    deleteDocument,
    isDocumentsPending,
    isDocumentsProcessing,
    uploadingDocuments,
    setUploadingDocuments,
    retryJuicing,
    summarizeDocument,
    refetchDocuments
  } = useDocuments(projectId, undefined, shouldFetchDocuments ? 3000 : null);

  const { documentsWithUsefulness, isDocumentsUsefulnessLoading, updateUsefulness } = useDocumentsUsefulness(
    projectId,
    chapterId,
    documents,
    shouldFetchDocuments
  );

  const { documentsWithPreferences, isContentPreferencesLoading, updatePreference } = useContentPreferences(
    projectId,
    chapterId,
    documents,
    shouldFetchDocuments
  );

  const [dialogOpenForId, setDialogOpenForId] = useState<number | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<number | null>(null);
  const [isTogglingContentType, setIsTogglingContentType] = useState(false);

  const handleDeleteClick = useCallback((id: number) => {
    setDialogOpenForId(id);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (dialogOpenForId === null) return;
    setIsDeletingId(dialogOpenForId as number);
    deleteDocument(dialogOpenForId as number).finally(() => setIsDeletingId(null));
  }, [dialogOpenForId, deleteDocument]);

  const documentsWithAllPreferences = documentsWithUsefulness.map((doc) => {
    const prefDoc = documentsWithPreferences.find((p) => p.id === doc.id);
    return {
      ...doc,
      contentType: prefDoc?.contentType || "FULL"
    };
  });

  const totalContentLength = documentsWithAllPreferences.reduce((total, doc) => {
    if (doc.usefulness === "USELESS") return total;
    if (doc.status !== "processed") return total;

    const lengthToUse =
      doc.contentType === "SUMMARIZED" && doc.summaryContentDisplay?.length
        ? doc.summaryContentDisplay?.length || 0
        : doc.fullContentDisplay?.length || 0;

    return total + lengthToUse;
  }, 0);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="max-w-[520px] ml-auto h-full flex flex-col">
        <DrawerHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <DrawerTitle className="font-medium">Vault</DrawerTitle>
            <DrawerClose asChild>
              <AppButton appearance="text" size="icon" className="size-6 p-0">
                <XIcon className="size-5" />
              </AppButton>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
          {(isLoading || isDocumentsUsefulnessLoading || isContentPreferencesLoading) && documents.length === 0 ? (
            <div className="h-full flex items-center justify-center gap-2">
              <LoadingSpinner />
              <Typography variant="body3">Loading documents...</Typography>
            </div>
          ) : documentsWithAllPreferences.length > 0 ? (
            <>
              <div className="mb-4 p-3 bg-innoveraZinc-50 rounded-lg border border-innoveraZinc-150">
                <div className="flex items-center justify-between">
                  <Typography variant="label2" className="font-medium">
                    Chapter Content Summary
                  </Typography>
                  <Typography variant="body3" color="zinc500">
                    Total: {formatCharacterLength(totalContentLength)}
                  </Typography>
                </div>
                <Typography variant="body3" color="zinc500" className="mt-1">
                  {documentsWithAllPreferences.filter((d) => d.usefulness !== "USELESS").length} useful documents •{" "}
                  {
                    documentsWithAllPreferences.filter(
                      (d) => d.contentType === "SUMMARIZED" && d.usefulness !== "USELESS"
                    ).length
                  }{" "}
                  using summaries
                </Typography>
              </div>

              <div className="flex flex-col gap-2">
                {documentsWithAllPreferences.map((d) => (
                  <FileItem
                    key={d.id}
                    uploadedBy={`${d.createdBy.firstName ?? "Unknown"} ${d.createdBy.lastName ?? ""}`}
                    size={d.fileSize}
                    name={d.fileName}
                    webUrl={d.webUrl}
                    documentId={d.id}
                    deleteDocument={() => handleDeleteClick(d.id)}
                    retryJuicing={async () => retryJuicing(projectId, d.id.toString())}
                    isDeleting={isDeletingId === d.id}
                    status={d.status}
                    usefulness={d.usefulness}
                    onChangeUsefulness={async (usefulness) => {
                      await updateUsefulness(d.id, usefulness);
                    }}
                    refetchDocuments={refetchDocuments}
                    isLoadingDocumentsUsefulness={isDocumentsUsefulnessLoading}
                    contentType={d.contentType}
                    onToggleContentType={async (contentType) => {
                      setIsTogglingContentType(true);
                      await updatePreference(d.id, contentType);
                      setIsTogglingContentType(false);
                    }}
                    onSummarize={async () => await summarizeDocument(d.id)}
                    extractedContent={d.extractedContent}
                    summarizedContent={d.summarizedContent}
                    fullContentDisplay={d.fullContentDisplay}
                    summaryContentDisplay={d.summaryContentDisplay}
                    isTogglingContentType={isTogglingContentType}
                    briefSummary={d.briefSummary?.summary}
                  />
                ))}
              </div>

              {uploadingDocuments.length > 0 && (
                <div className="flex items-center my-2 text-xs py-1 px-2 bg-blue-100 text-blue-800 rounded">
                  <div className="h-3 w-3 border-2 border-t-transparent border-blue-500 rounded-full animate-spin mr-2 flex-shrink-0"></div>
                  <span className="truncate">{`Uploading: ${uploadingDocuments.join(", ")}`}</span>
                </div>
              )}
            </>
          ) : (
            <div className="h-full flex flex-col justify-center items-center text-center">
              <Typography variant="body3" className="text-innoveraZinc-500">
                No documents in vault
              </Typography>
            </div>
          )}
        </div>

        <div className="border-t p-2">
          <UploadFilesButton
            createDocument={(file: File) => createDocument(file, chapterId)}
            createWebLink={(url: string) => createWebLink(url, chapterId)}
            setUploadingDocuments={setUploadingDocuments}
            isDocumentsPending={isDocumentsPending}
            isDocumentsProcessing={isDocumentsProcessing}
          />
        </div>

        <ConfirmationModal
          isOpen={dialogOpenForId !== null}
          onClose={() => setDialogOpenForId(null)}
          onConfirm={handleConfirmDelete}
          title="Delete Document"
          description="Are you sure you want to delete this document?"
        />
      </DrawerContent>
    </Drawer>
  );
}
